import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, User, Mail, Lock } from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

export default function BossSettingUpdateModal({ visible, onClose }) {
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const parsed = JSON.parse(user);
      setUserData(parsed);
      setName(parsed.name || "");
    }
  }, [visible]);

  const handleClose = () => {
    if (userData) setName(userData.name || "");
    setPassword("");
    onClose();
  };

  const handleUpdate = async () => {
    if (!name.trim()) {
      alert(t("nameRequired"));
      return;
    }
    if (!userData) {
      alert(t("userDataNotFound"));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${MyDomain}Users3/UpdateUser`, {
        Id: userData.id,
        Name: name,
        Email: userData.email,
        Password: password || userData.password,
      });

      if (response.data) {
        const updatedUser = {
          ...userData,
          name: response.data.newName || name,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUserData(updatedUser);
        alert(response.data.Message || t("profileUpdatedSuccessfully"));
        onClose();
      }
    } catch (error) {
      console.error("Update error:", error);
      let errorMessage = t("failedToUpdateProfile");
      if (error.response) {
        const responseMessage =
          error.response.data?.message || error.response.data;
        if (
          responseMessage &&
          (responseMessage.includes("already taken") ||
            responseMessage.includes("already exists"))
        ) {
          errorMessage =
            "This name is already taken. Please choose another name.";
        } else {
          errorMessage = responseMessage || errorMessage;
        }
      }
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg px-5 pt-5 pb-10 max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">{t("updateProfile")}</h2>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* ── Form ── */}
        <div className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-600 ml-1">
              {t("name")} *
            </label>
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 focus-within:border-sky-400 transition">
              <User className="w-5 h-5 text-slate-400 mr-2 flex-shrink-0" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("name")}
                className="flex-1 bg-transparent py-3.5 text-base text-slate-800 outline-none"
              />
            </div>
          </div>

          {/* Email — display only, not editable */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 ml-1">
              <label className="block text-sm font-semibold text-slate-400">
                {t("email")}
              </label>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
                {t("cannotBeChanged")}
              </span>
            </div>
            <div className="flex items-center bg-slate-100 border border-slate-200 rounded-xl px-3 cursor-not-allowed">
              <Mail className="w-5 h-5 text-slate-300 mr-2 flex-shrink-0" />
              <span className="flex-1 py-3.5 text-base text-slate-400 select-none">
                {userData?.email || "—"}
              </span>
              <Lock className="w-4 h-4 text-slate-300 flex-shrink-0" />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-600 ml-1">
              {t("password")}
            </label>
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 focus-within:border-sky-400 transition">
              <Lock className="w-5 h-5 text-slate-400 mr-2 flex-shrink-0" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("enterNewPasswordOptional")}
                className="flex-1 bg-transparent py-3.5 text-base text-slate-800 outline-none"
              />
            </div>
            <p className="text-xs text-slate-400 italic ml-1">
              {t("leaveBlankToKeepPassword")}
            </p>
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold text-base transition disabled:opacity-50"
          >
            {t("cancel")}
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading}
            className={`flex-1 py-3.5 rounded-xl font-semibold text-base text-white transition ${
              loading
                ? "bg-sky-300 cursor-not-allowed"
                : "bg-sky-500 hover:bg-sky-600"
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t("updating")}
              </div>
            ) : (
              t("updateProfile")
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
